import { uploadFile } from "../componets/SampleFiles/UploadFileButton";
import { uploadFilePredictClass } from "../Interfaces";
import { ATS_API_URL } from "../config";
import baseDataProvider, { fetchWithAuth, httpClient } from "./baseDataProvider";
import createMethod from "./methods/create";
import { getDocumentUploadPath } from "./documentUploadEndpoint";
import getOneMethod from "./methods/getOne";
import getList from "./methods/getList";
import update from "./methods/update";
import getMany from "./methods/getMany";
import { openWithPopupCheck } from "../utils/popupWindow";

const API_URL = ATS_API_URL;
const API_URL_DOC = `${API_URL}/applicant`;

// Backend error bodies are inconsistent about whether `message` is present or
// even a string — fall back to a generic message rather than surfacing
// `undefined`/an object to the user.
const toServerMessage = (error: any): string => {
  const serverMessage = error?.body?.message;
  return typeof serverMessage === "string" && serverMessage.trim()
    ? serverMessage
    : "something went wrong, try again!";
};

// Preserves the HTTP status (fetchJson sets it on the thrown error) so
// callers — e.g. the bulk document move/delete retry logic — can distinguish
// a definitive 4xx from a transient failure instead of always retrying.
const toRejectedError = (error: any) => ({
  status: error?.status,
  message: toServerMessage(error),
});

const normalizeRaResult = (action: "create" | "getOne", resource: string, params: any, result: any) => {
  if (!result || typeof result !== "object") {
    const normalized = { data: { id: params?.id ?? params?.data?.id ?? params?.data?.trn_id ?? `${action}:${resource}` } };
    console.error(`[dataProvider:${action}] invalid result shape for resource=${resource}`, { result, normalized });
    return normalized;
  }

  const data = result.data;
  if (!data || typeof data !== "object") {
    const normalizedData = {
      ...(data && typeof data === "object" ? data : {}),
      id: params?.id ?? params?.data?.id ?? params?.data?.trn_id ?? `${action}:${resource}`,
    };
    console.error(`[dataProvider:${action}] missing data object for resource=${resource}`, { result, normalizedData });
    return { ...result, data: normalizedData };
  }

  if (data.id === undefined || data.id === null || data.id === "") {
    return {
      ...result,
      data: {
        ...data,
        id: params?.id ?? params?.data?.id ?? params?.data?.trn_id ?? `${action}:${resource}`,
      },
    };
  }

  return result;
};

export const dataProvidersAts = {
  ...baseDataProvider,
  create: async (resource, params) => {
    const result = await createMethod(resource, params);
    return normalizeRaResult("create", resource, params, result);
  },
  getOne: async (resource, params) => {
    const result = await getOneMethod(resource, params);
    return normalizeRaResult("getOne", resource, params, result);
  },
  delete: async (resource, params) => {
    if (resource == "getocrqueuelist") {
      try {
        const { json, headers, status }: any = await httpClient(
          API_URL + "/admin/removefromqueue?id=" + params.id,
          {
            method: "POST",
          }
        );
        const r = json;
        const res = {
          data: { id: params.id },
        };
        return res;
      } catch (e: any) {
        throw new Error(e.message);
      }
    }
    if(resource == "mlclassdetails"){
      try {
        const { json, headers, status }: any = await httpClient(
          API_URL_DOC + `/ml/${resource}/${params.id}`,
          {
            method: "DELETE",
          }
        );
        const r = json;
        const res = {
          data: { ...r },
        };
        return res;
      } catch (e: any) {
        throw new Error(e.message);
      }
    }
    if (resource == "predefinedmessage") {
      try {
        const { json, headers, status }: any = await httpClient(
          API_URL + "/subscriber/deletepredefinedmessages/" + params.id,
          {
            method: "DELETE",
          }
        );
        const r = json;
        const res = {
          data: { ...r },
        };
        return res;
      } catch (e: any) {
        throw new Error(e.message);
      }
    }
    if (resource == "predefinedmessageAdmin") {
      try {
        const { json, headers, status }: any = await httpClient(
          API_URL + "/admin/predefinedmessages/" + params.id,
          {
            method: "DELETE",
          }
        );
        return json;
      } catch (e: any) {
        throw new Error(e.message);
      }
    }
    if (resource == "deletedDocument") {
      try {
        const { json, headers, status }: any = await httpClient(
          API_URL_DOC + "/document/deletedDocument/" + params.id,
          {
            method: "DELETE",
          }
        );
        const r = json;
        const res = {
          data: { id: params.id },
        };
        return res;
      } catch (e: any) {
        throw new Error(e.message);
      }
    }
    if (resource == "getSystemNotification") {
      try {
        const { json, headers, status }: any = await httpClient(
          API_URL + "/admin/deleteSystemNotification/" + params.id,
          {
            method: "DELETE",
          }
        );
        const r = json;
        const res = {
          data: { id: params.id },
        };
        return res;
      } catch (e: any) {
        throw new Error(e.message);
      }
    }
    if (resource.includes("/applicant/")) {
      try {
        const { json, headers, status }: any = await httpClient(
          API_URL_DOC + `${resource}/${params.id}`,
          {
            method: "DELETE",
          }
        );
        const r = json;
        const res = {
          data: { id: "" },
        };
        return res;
      } catch (e: any) {
        throw new Error(e.message);
      }
    }
    return baseDataProvider.delete(resource, params);
  },
  getList,
  getMany,
  update,
  insertSequence: async (payload) => {
    try {
      const { json }: any = await httpClient(API_URL_DOC + `/ml/insertSequence`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const r = json;
      const res = {
        data: { ...r.records },
      };
      return res;
    } catch (e: any) {
      throw new Error(e.message);
    }
  },
  getSubscriberDetails: async (id) => {
    try {
      const { json, headers, status }: any = await httpClient(
        API_URL_DOC + `/users/logo/${id}`
      );
      const r = json;
      const res = {
        data: { ...r.records },
      };
      return res;
    } catch (e: any) {
      throw new Error(e.message);
    }
  },
  deleteSource: async (resource, payload) => {
    const subUrl = payload.subUrl;
    delete payload.subUrl;
    const raw = JSON.stringify({ ...payload });
    if (resource === "income") {
      try {
        const { json, headers, status }: any = await httpClient(
          API_URL_DOC + `/${subUrl}`,
          {
            method: "DELETE",
            body: raw,
          }
        );
        const r = json;
        const res = {
          data: { ...r.records },
        };
        return res;
      } catch (e: any) {
        throw new Error(e.message);
      }
    }
    try {
      const { json }: any = await httpClient(API_URL_DOC + `/${subUrl}`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const r = json;
      const res = {
        data: { ...r.records },
      };
      return res;
    } catch (e: any) {
      throw new Error(e.message);
    }
  },
  deleteSequence: async (resource, payload) => {
    const subUrl = payload.subUrl;
    delete payload.subUrl;
    const raw = JSON.stringify({ ...payload });
    if (resource === "income") {
      try {
        const { json, headers, status }: any = await httpClient(
          API_URL_DOC + `/ml${subUrl}`,
          {
            method: "DELETE",
            body: raw,
          }
        );
        const r = json;
        const res = {
          data: { ...r.records },
        };
        return res;
      } catch (e: any) {
        throw new Error(e.message);
      }
    }
    try {
      const { json }: any = await httpClient(API_URL_DOC + `/ml${subUrl}`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const r = json;
      const res = {
        data: { ...r.records },
      };
      return res;
    } catch (e: any) {
      throw new Error(e.message);
    }
  },
  checkEmailExist: async (params) => {
    try {
      const { json }: any = await httpClient(
        `${API_URL}/common/checkemailexist?${params?.email
          ? `email=${params?.email}`
          : params?.username
            ? `username=${params?.username}`
            : null
        }`,
        { method: "GET" }
      );
      const res = await json;
      return {
        data: { ...res },
      };
    } catch (error) {
      throw new Error("something went wrong, try again!");
    }
  },
  generateXml: async (payload) => {
    try {
      const { json }: any = await httpClient(
        API_URL_DOC + `/ml/generateXmlForDocuments`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        }
      );
      const r = json;
      const res = {
        data: { ...r },
      };
      return res;
    } catch (e: any) {
      throw new Error(e.message);
    }
  },
  uploadFile: async (myFromData: uploadFile) => {
    try {
      const formdata = new FormData();
      myFromData.files.forEach((file, item) => {
        formdata.append("files", file);
      });
      formdata.append("class_id", myFromData.classId);
      const response: any = await httpClient(API_URL_DOC + "/ml/addpdf", {
        method: "POST",
        body: formdata,
      });
      return response.json;
    } catch (e: any) {
      throw new Error(e.message);
    }
  },
  uploadFilePredictClass: async (myFromData: uploadFilePredictClass) => {
    try {
      const formdata = new FormData();
      formdata.append("files", myFromData.files);
      formdata.append("documenttype_id", myFromData.docTypeId);

      const res: any = await httpClient(API_URL_DOC + "/ml/predictclass", {
        method: "POST",
        body: formdata,
      });
      return res.json;
    } catch (e: any) {
      throw new Error(e.message);
    }
  },
  uploadDocument: (myFromData) => {
    const formdata = new FormData();
    formdata.append("trn_id", myFromData.id);
    formdata.append("files", myFromData.files);
    formdata.append("document_id", myFromData.document_id);
    formdata.append("source_id", myFromData.source_id);
    formdata.append("allowOfferLetter", "null");
    const uploadPath = getDocumentUploadPath(myFromData.document_id);

    return httpClient(API_URL + uploadPath, {
      method: "POST",
      body: formdata,
    })
      .then((response: any) => response.json)
      .catch((error: any) => Promise.reject(toRejectedError(error)));
  },
  deleteDocument: (id) => {
    return httpClient(API_URL_DOC + `/applicant/deletedocument/${id}`, {
      method: "DELETE",
    })
      .then((response) => ({ data: { id: id } }))
      .catch((error: any) => Promise.reject(toRejectedError(error)));
  },
  deleteDocumentAllIds: (id) => {
    return httpClient(API_URL_DOC + `/applicant/deleteiddocument/${id}`, {
      method: "DELETE",
    })
      .then((response) => ({ data: { id: id } }))
      .catch((error: any) => Promise.reject(toRejectedError(error)));
  },
  deleteDocumentNoDoc: (id) => {
    return httpClient(API_URL_DOC + `/applicant/deletepaystub/${id}/0`, {
      method: "DELETE",
    })
      .then((response) => ({ data: { id: id } }))
      .catch((error: any) => Promise.reject(toRejectedError(error)));
  },

  disableValidationMessage: async (payload) => {
    try {
      const raw = JSON.stringify({ ...payload });
      const { json, headers, status }: any = await httpClient(
        API_URL_DOC + `/document/updateValidationMessages`,
        {
          method: "POST",
          body: raw,
        }
      );
      const res = {
        data: { ...json },
      };
      return res;
    } catch (e: any) {
      throw new Error(e.message);
    }
  },
  getLoggedUser: async (payload) => {
    try {
      const raw = JSON.stringify({ ...payload });
      const { json, headers, status }: any = await httpClient(
        API_URL_DOC + "/auth/superuser",
        {
          method: "POST",
          body: raw,
        }
      );
      const res = {
        data: { ...json },
      };

      return res;
    } catch (e: any) {
      throw new Error(e.message);
    }
  },

  verifyOtp: async (payload) => {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    const raw = JSON.stringify(payload);
    try {
      const result = await fetch(API_URL_DOC + "/auth/VerifyOtp", {
        method: "POST",
        body: raw,
        headers: myHeaders,
      });
      if (!result.ok) {
        throw new Error(`Error! status: ${result.status}`);
      }
      const res = await result.json();
      return { data: { ...res } };
    } catch (e: any) {
      throw new Error(e.message);
    }
  },
  paystubProcessing: async (payload: any) => {
    const subUrl = payload.subUrl;
    const method = payload?.method || "DELETE";
    // delete payload?.method;
    delete payload.subUrl;
    const raw = JSON.stringify({ ...payload });
    try {
      const { json, headers, status }: any = await httpClient(
        API_URL_DOC + `${subUrl}`,
        {
          method: method,
          body: raw,
        }
      );
      return { data: { ...json } };
    } catch (e: any) {
      throw new Error(e.message);
    }
  },
  downloadZip: async (payload: any) => {
    try {
      const { data } = JSON.parse(<any>localStorage.getItem("auth")) || {};

      const headers = new Headers();
      headers.append("authorization", data?.tokendata || null);
      headers.append("trn_id", data?.trn_id || null);

      // Make the fetch request with the headers
      const response = await fetch(
        API_URL + "/common/getdownloadalldocs/" + payload,
        {
          method: "GET",
          headers: headers,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch ZIP file");
      }

      // Convert the response body to a Blob
      const blob = await response.blob();

      // Create a Blob URL for the ZIP file
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link element and trigger the download
      const a = document.createElement("a");
      a.href = url;
      a.download = "docszip.zip"; // Set the desired file name
      a.click();

      // Clean up by revoking the URL object
      window.URL.revokeObjectURL(url);
      // const { body }: any = await httpClient(API_URL + '/common/getdownloadalldocs/' + payload);
      return { data: response };
    } catch (error: any) {
      throw new Error(error.message);
    }
  },
  // ADO 4853: the official, immutable submission snapshot's PDF — rendered
  // fresh from its frozen JSON on every call and scoped by current viewer
  // permissions. The existingWindow handoff avoids delayed popup blocking.
  // ADO 4855: `content` selects results-only (default) / with the Document
  // Portfolio appendix / portfolio-only, per the caller's saved preference.
  viewVrReportSnapshot: async (payload: {
    trnId: string;
    onPopupBlocked?: () => void;
    targetWindow?: Window | null;
    openInNewWindow?: boolean;
    content?: "results" | "results_with_portfolio" | "portfolio_only";
  }) => {
    try {
      const contentParam = payload.content
        ? `&content=${encodeURIComponent(payload.content)}`
        : "";
      const response = await fetchWithAuth(
        `${API_URL_DOC}/document/viewVrReportSnapshot?trn_id=${encodeURIComponent(payload.trnId)}${contentParam}`,
        { method: "GET" }
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        throw new Error(
          errorBody?.error || "Failed to fetch the submitted report"
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      if (payload.openInNewWindow !== false) {
        openWithPopupCheck(url, "_blank", undefined, {
          onBlocked: payload.onPopupBlocked,
          existingWindow: payload.targetWindow,
        });
      }

      return { data: { success: true, url } };
    } catch (error: any) {
      throw new Error(error.message);
    }
  },
  downloadSampleXls: async (payload: { sampleType: string }) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/subscriber/donwload-sample-xls?sampleType=${encodeURIComponent(payload?.sampleType || "")}`,
        { method: "GET" }
      );
      if (!response.ok) {
        throw new Error(`Failed to download sample (${response.status})`);
      }
      const contentDisposition = response.headers.get("content-disposition") || "";
      const filenameMatch = contentDisposition.match(
        /filename\*=UTF-8''([^;]+)|filename=\"?([^\";]+)\"?/i
      );
      const fileName = decodeURIComponent(
        filenameMatch?.[1] || filenameMatch?.[2] || `${payload?.sampleType || "sample"}ImportSample.xlsx`
      );
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      return { data: { objectUrl, fileName } };
    } catch (error: any) {
      throw new Error(error.message);
    }
  },
  validateInvitation: async (payload: any) => {
    try {
      const headers = new Headers();
      headers.append("auth", payload?.auth || null);

      // Make the fetch request with the headers
      const response = await httpClient(
        API_URL + "/common/validate-invitation",
        {
          method: "GET",
          headers: headers,
        }
      );
      return { data: response };
    } catch (error: any) {
      throw new Error(error.message);
    }
  },
};
